import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Diskret "Lyt"-knap – cirkel-outline med play-trekant + kursiv "Lyt · X min".
 * Henter en varm, rolig læserstemme via Lovable AI Gateway (se /api/tts).
 * Skjules helt hvis TTS-tokens/credits mangler.
 */

let availabilityPromise: Promise<boolean> | null = null;
function checkAvailability(): Promise<boolean> {
  if (!availabilityPromise) {
    availabilityPromise = fetch("/api/tts", { method: "GET" })
      .then((r) => (r.ok ? r.json() : { available: false }))
      .then((j) => Boolean(j?.available))
      .catch(() => false);
  }
  return availabilityPromise;
}

// Groft estimat: ca. 155 ord/minut ved rolig oplæsning.
function estimateMinutes(text: string): number {
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 155));
}

export function ListenButton({
  text,
  label = "Lyt",
  className = "",
  voice = "male",
  variant = "onLight",
}: {
  text: string;
  label?: string;
  className?: string;
  voice?: "male" | "female";
  variant?: "onLight" | "onDark";
}) {
  const [available, setAvailable] = useState<boolean | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "playing" | "paused" | "error">("idle");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  const cleanup = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);
  useEffect(() => {
    let cancelled = false;
    checkAvailability().then((ok) => {
      if (!cancelled) setAvailable(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    cleanup();
    setStatus("idle");
  }, [text, cleanup]);

  const handleClick = useCallback(async () => {
    if (status === "playing") {
      audioRef.current?.pause();
      setStatus("paused");
      return;
    }
    if (status === "paused" && audioRef.current) {
      await audioRef.current.play().catch(() => setStatus("error"));
      setStatus("playing");
      return;
    }
    if (status === "loading") return;

    setStatus("loading");
    try {
      const res = await fetch("/api/tts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, voice }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const ct = res.headers.get("content-type") ?? "";
      if (ct.includes("application/json")) {
        const info = await res.json().catch(() => ({}));
        console.warn("[ListenButton] oplæsning utilgængelig:", info);
        setAvailable(false);
        availabilityPromise = Promise.resolve(false);
        return;
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setStatus("idle");
      audio.onerror = () => setStatus("error");
      await audio.play();
      setStatus("playing");
    } catch (e) {
      console.error("[ListenButton] fejl:", e);
      setStatus("error");
    }
  }, [status, text, voice]);

  const busy = status === "loading";
  const isPlaying = status === "playing";
  const isPaused = status === "paused";
  const minutes = estimateMinutes(text);

  const title = busy
    ? "Henter oplæsning …"
    : isPlaying
      ? "Pause"
      : isPaused
        ? "Fortsæt"
        : status === "error"
          ? "Prøv igen"
          : `${label} · ${minutes} min`;

  if (available === false) return null;

  const onDark = variant === "onDark";
  const fg = onDark ? "text-accent-onDark hover:text-paper" : "text-accent hover:text-accent-dark";
  const ringColor = onDark ? "border-accent-onDark" : "border-accent";
  const triFill = onDark ? "border-l-accent-onDark" : "border-l-accent";

  const labelText = busy
    ? "Henter oplæsning"
    : isPlaying
      ? "Pause"
      : isPaused
        ? "Fortsæt"
        : `${label} · ${minutes} min`;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      aria-label={title}
      title={title}
      aria-busy={busy}
      className={`inline-flex items-center gap-2.5 bg-transparent border-0 p-0 cursor-pointer font-serif italic font-light text-[14px] md:text-[15px] whitespace-nowrap transition-colors duration-200 disabled:opacity-80 disabled:cursor-default ${fg} ${className}`}
    >
      <span
        aria-hidden="true"
        className={`inline-flex items-center justify-center rounded-full border ${ringColor} w-[28px] h-[28px] shrink-0`}
      >
        {busy ? (
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="animate-spin"
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        ) : isPlaying ? (
          <span className="flex gap-[3px]">
            <span className="w-[2px] h-[10px] bg-current" />
            <span className="w-[2px] h-[10px] bg-current" />
          </span>
        ) : (
          <span
            className={`w-0 h-0 border-y-[5px] border-y-transparent border-l-[8px] ${triFill} ml-[2px]`}
          />
        )}
      </span>
      {labelText}
    </button>
  );
}
